import React, { useState } from "react";
import { Button, Typography, Box, CircularProgress, Modal, IconButton, useMediaQuery, useTheme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import Layout from "../../components/Layout";
import BeginSurveyCard from "../../components/BeginSurveyCard";
import { useAppContext } from "../../context/AppContext";
import { COMMON_MUI_STYLES } from "../../styles";
import ApiService from "../../services/api";
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
// import { Messages } from '../services/messages';
import { logo } from "../../assets";
import GreenIcon from "../../assets/Green_icon.png";
import YellowIcon from "../../assets/Yellow_icon.png";
import RedIcon from "../../assets/Red_icon.png";
import { getDistributorInfoAndName } from '../../utils/tokenUtils';

// Configure PDF.js worker - loaded from public/pdfjs folder (similar to smart-capture)
pdfjs.GlobalWorkerOptions.workerSrc = '/pdfjs/pdf.worker.min.mjs';

const FeedbackPage: React.FC = () => {
  const { setFeedback, setSnackbar, sessionId, setSessionId, setCurrentView, role, userProfilesCache, getTranslation, currentLanguage } =
    useAppContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [rating, setRating] = useState<"GREEN" | "RED" | "YELLOW" | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPdfViewLoading, setIsPdfViewLoading] = useState(false);
  const [isPdfDownloadLoading, setIsPdfDownloadLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pdfLoadError, setPdfLoadError] = useState<string | null>(null);

  const submitFeedback = async (selectedRating: "GREEN" | "RED" | "YELLOW"): Promise<void> => {
    if (isSubmitted || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    setRating(selectedRating);
    setFeedback(selectedRating);

    const api = new ApiService(sessionId);
    try {
      await api.submitFeedback(selectedRating);
      setIsSubmitted(true);
      setSnackbar({
        open: true,
        message: getTranslation("Txt904" ),
        severity: "success",
      });
    } catch {
      setSnackbar({
        open: true,
        message: getTranslation("Err87"),
        severity: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleViewPdfBrochure = async (): Promise<void> => {
    try {
      setIsPdfViewLoading(true);
      const api = new ApiService();
      const selectedLanguage = currentLanguage || 'en';
      
      const response = await api.getPdfBrochureTranslations(selectedLanguage);
      
      if (!response.data?.pdf_brochure) {
        throw new Error('PDF URL not found');
      }

      // Set PDF URL and show modal with iframe
      setPdfUrl(response.data.pdf_brochure);
      setShowPdfModal(true);
     
    } catch (error) {
      console.error('Error fetching PDF brochure:', error);
      setSnackbar({
        open: true,
        message: getTranslation("Err88", "Failed to load PDF brochure"),
        severity: "error",
      });
    } finally {
      setIsPdfViewLoading(false);
    }
  };

  const handleDownloadPdfBrochure = async (): Promise<void> => {
    try {
      setIsPdfDownloadLoading(true);
      const api = new ApiService();
      const selectedLanguage = currentLanguage || 'en';
      
      const response = await api.getPdfBrochureTranslations(selectedLanguage);
      
      if (!response.data?.pdf_brochure) {
        throw new Error('PDF URL not found');
      }

      // Fetch the PDF as a blob
      const pdfResponse = await fetch(response.data.pdf_brochure);
      if (!pdfResponse.ok) {
        throw new Error('Failed to fetch PDF');
      }
      
      const blob = await pdfResponse.blob();
      
      // Create a new blob with application/octet-stream type to force download
      // This prevents Safari from recognizing it as PDF and opening it
      // Note: Server should also set Content-Type: application/octet-stream header
      const downloadBlob = new Blob([blob], { type: 'application/octet-stream' });
      
      // Create blob URL and trigger direct download
      const url = window.URL.createObjectURL(downloadBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `brochure-${selectedLanguage}.pdf`;
      link.setAttribute('download', `brochure-${selectedLanguage}.pdf`);
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Show success message
      setSnackbar({
        open: true,
        message: getTranslation("Txt969", "PDF downloaded successfully"),
        severity: "success",
      });
      
      // Clean up the blob URL after a delay
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 100);
     
    } catch (error) {
      console.error('Error downloading PDF brochure:', error);
    } finally {
      setIsPdfDownloadLoading(false);
    }
  };

  const handleClosePdfModal = () => {
    setShowPdfModal(false);
    setPdfUrl(null);
    setNumPages(null);
    setPdfLoadError(null);
  };

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setPdfLoadError(null);
  };

  const onDocumentLoadError = (error: Error) => {
    console.error('Error loading PDF:', error);
    setPdfLoadError('Failed to load PDF. Please try again.');
  };


  const { distributorInfo, distributorName } = getDistributorInfoAndName(userProfilesCache.data);

  return (
    <Layout showBackButton={false}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, paddingBottom: 4 }}>
      <Box
        sx={{
          textAlign: "center",
          // px: { xs: 2, sm: 3, md: 4 },
          maxWidth: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: { xs: "12px", md: "15px" },
        }}
      >
        <Box
          component="img"
          src={logo}
          alt="Company Logo"
          sx={{
            height: { xs: "2rem", sm: "2.5rem", md: "3rem" },
            width: "100%",
            objectFit: "contain",
          }}
        />
    
        <Typography
          variant="h5"
          component="h1"
          gutterBottom
          sx={{
            paddingTop: "10px",
            fontSize: { xs: "1.25rem", sm: "1.5rem", md: "1.75rem" },
            fontWeight: 700,
            mb: 1,
          }}
        >
          {getTranslation("Txt459")}
        </Typography>
        {role === 2 && (
        <Typography
          variant="body1"
          sx={{
            mb: 1,
            fontSize: { xs: "1rem", sm: "1rem" },
            color: "var(--color-text-primary)",
            fontWeight: 700,
          }}
        >
          {getTranslation("Txt460")}
        </Typography>
        )}
        {distributorInfo && (
        <Box
          sx={{
            mb: 2,
            p: { xs: 2, sm: 2.5, md: 3 },
            mx: "auto",
            width: { xs: "95%", sm: "90%", md: "600px" },
            maxWidth: { xs: "95%", sm: "90%", md: "600px" },
            minWidth: { xs: "95%", sm: "90%", md: "600px" },
            background: "#ffffff",
            borderRadius: 3,
            border: "1px solid #e5e5e5",
            boxShadow: "0 4px 16px rgba(0, 0, 0, 0.08)",
            transition: "all 0.3s ease-in-out",
            boxSizing: "border-box",
          }}
        >
          <Typography
            variant="body1"
            sx={{
              mb: 2,
              fontSize: { xs: "0.95rem", sm: "1rem", md: "1.05rem" },
              color: "var(--color-text-primary)",
              lineHeight: 1.5,
              fontWeight: 500,
              textAlign: "center",
            }}
          >
            {getTranslation("Txt75").replace(/\[.*?\]/g, distributorName)}
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {(() => {
              const profile = userProfilesCache.data as any;
              if (!profile) return null;
              
              const distributorInfo = profile?.user?.distributor_info;
              const distributorEmail = distributorInfo?.primary_email || 'N/A';
              const distributorPhone = distributorInfo?.phone_number || 'N/A';

              return (
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: { xs: "0.9rem", sm: "0.95rem" },
                      color: "#495057",
                      fontWeight: 500,
                      textAlign: "center",
                    }}
                  >
                    <strong>{getTranslation("Txt76", "Email : [Distributor's Email]").split(':')[0] + ':'}</strong> {distributorEmail}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: { xs: "0.9rem", sm: "0.95rem" },
                      color: "#495057",
                      fontWeight: 500,
                      textAlign: "center",
                    }}
                  >
                    <strong>{getTranslation("Txt77", "Phone Number : [Distributor's Phone Number]").split(':')[0] + ':'}</strong> {distributorPhone}
                  </Typography>
                </Box>
              );
            })()}
          </Box>
        </Box>
        )}
      </Box>
      <Box
        sx={{
          display: "inline-flex",
          flexDirection: "column",
          alignItems: "stretch",
          mx: "auto",
          width: { xs: "100%", md: "auto" },
        }}
      >
      <Box
        sx={{
          mb: 2,
          p: { xs: 2, sm: 2.5, md: 3 },
          mx: "auto",
          width: "auto",
          alignSelf: "stretch",
          // maxWidth: { xs: "95%", sm: "90%", md: "600px" },
          minWidth: { xs: "95%", sm: "90%", md: "600px" },
          background: "#ffffff",
          borderRadius: 3,
          border: "1px solid #e5e5e5",
          textAlign: "center",
          boxShadow: "0 4px 16px rgba(0, 0, 0, 0.08)",
          transition: "all 0.3s ease-in-out",
          boxSizing: "border-box",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontSize: { xs: "1.15rem", sm: "1.3rem", md: "1.4rem" },
            fontWeight: 700,
            color: "var(--color-text-primary)",
            mb: 1.5,
            lineHeight: 1.3,
          }}
        >
         {getTranslation("Txt913")}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { xs: "0.95rem", sm: "1rem", md: "1.05rem" },
            color: "var(--color-text-secondary)",
            mb: 3,
            lineHeight: 1.5,
            fontWeight: 400,
          }}
        >
          {getTranslation("Txt914")}
        </Typography>
        <Box
          sx={{
            display: { xs: "flex", sm: "inline-grid" },
            flexDirection: { xs: "column", sm: "row" },
            gridTemplateColumns: { xs: "none", sm: "max-content max-content" },
            gap: { xs: 1.5, sm: 2 },
            justifyItems: "stretch",
            justifyContent: "center",
            mx: { xs: "auto", sm: "auto" },
            width: { xs: "100%", sm: "auto" },
            alignItems: "center",
          }}
        >
          <Button
            variant="contained"
            onClick={handleViewPdfBrochure}
            disabled={isPdfViewLoading || isPdfDownloadLoading}
            sx={{
              ...COMMON_MUI_STYLES.primaryCtaButton2,
              position: 'relative',
              '& > *:not(.loader-overlay)': {
                opacity: isPdfViewLoading ? 0 : 1,
                transition: 'opacity 0.2s ease-in-out',
              },
            }}
          >
            <span>{getTranslation("Txt967", "View Skin Brochure")}</span>
            {isPdfViewLoading && (
              <CircularProgress 
                size={20} 
                sx={{ 
                  color: "white", 
                  position: 'absolute', 
                  top: '50%', 
                  left: '50%', 
                  marginTop: '-10px',
                  marginLeft: '-10px',
                }} 
                className="loader-overlay"
              />
            )}
          </Button>
          <Button
            variant="contained"
            onClick={handleDownloadPdfBrochure}
            disabled={isPdfViewLoading || isPdfDownloadLoading}
            startIcon={isPdfDownloadLoading ? <CircularProgress size={16} sx={{ color: "white" }} /> : <DownloadIcon sx={{ fontSize: "17px" }} />}
            sx={{
              ...COMMON_MUI_STYLES.primaryCtaButton2,
              display: "flex",
              flexDirection: "row-reverse",
              gap: "5px",
              "& .MuiButton-startIcon": {
                marginRight: 0,
                marginLeft: 0,
              },
            }}
          >
            {getTranslation("Txt966", "Download")}
          </Button>
        </Box>
      </Box>
        {/* {role === 2 && ( */}
          <Box sx={{ width: "auto", alignSelf: "stretch" }}>
          <BeginSurveyCard
            title={getTranslation("Txt955")}
            buttonText={getTranslation("Txt956")}
            role={role}
            currentLanguage={currentLanguage}
          />
          </Box>
        {/* )} */}
          {/* } */}
      <Box
        component="div"
        sx={{
          width: "auto",
          alignSelf: "stretch",
          // maxWidth: { xs: "95%", sm: "90%", md: "600px" },
          minWidth: { xs: "95%", sm: "90%", md: "600px" },
          mx: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 1,
          p: { xs: 2, sm: 2.5, md: 3 },
          background: "#ffffff",
          borderRadius: 3,
          border: "1px solid #e5e5e5",
          boxShadow: "0 4px 16px rgba(0, 0, 0, 0.08)",
          transition: "all 0.3s ease-in-out",
          boxSizing: "border-box",
        }}
      >
        <Typography
          variant="h6"
          className="center"
          sx={{
            fontSize: { xs: "1.15rem", sm: "1.3rem", md: "1.4rem" },
            fontWeight: 700,
            color: "var(--color-text-primary)",
            lineHeight: 1.3,
            textAlign: "center",
          }}
        >
          {getTranslation("Txt739")}
        </Typography>

        <Box
          className="feedback-rating"
          sx={{
            display: "flex",
            flexDirection: { xs: "row", sm: "row" },
            justifyContent: "center",
            alignItems: "center",
            gap: { xs: 1.5, sm: 2 },
            width: "100%",
          }}
        >

          <Box
            className={`${
              rating === "GREEN" ? "selected" : ""
            } feedback-rating-star`}
            onClick={() => {
              if (!isSubmitted && !isSubmitting) {
                submitFeedback("GREEN");
              }
            }}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
              borderRadius: 2,
              p: 1.5,
              background: rating === "GREEN" ? "rgba(0, 184, 28, 0.05)" : "transparent",
              border: rating === "GREEN" ? "1px solid rgba(0, 184, 28, 0.2)" : "1px solid transparent",
              transition: "all 0.3s ease-in-out",
              opacity: isSubmitted ? 0.6 : 1,
              cursor: isSubmitted ? "not-allowed" : "pointer",
              "&:hover": {
                background: !isSubmitted ? "rgba(0, 184, 28, 0.02)" : undefined,
                transform: !isSubmitted ? "translateY(-1px)" : undefined,
              },
            }}
          >
            <Box
              component="img"
              src={GreenIcon}
              alt="Green"
              sx={{
                width: '60px',
                height: '60px',
                objectFit: 'contain',
              }}
            />
            <Typography sx={{ 
              fontSize: { xs: "0.9rem", sm: "0.95rem" }, 
              fontWeight: 500,
              color: "var(--color-text-secondary)",
              textAlign: "center"
            }}>
              {getTranslation("Txt742")}
            </Typography>
          </Box>
       
        <Box
            className={`${
              rating === "YELLOW" ? "selected" : ""
            } feedback-rating-star`}
            onClick={() => {
              if (!isSubmitted && !isSubmitting) {
                submitFeedback("YELLOW");
              }
            }}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
              p: 1.5,
              borderRadius: 2,
              background: rating === "YELLOW" ? "rgba(255, 236, 2, 0.05)" : "transparent",
              border: rating === "YELLOW" ? "1px solid rgba(255, 236, 2, 0.3)" : "1px solid transparent",
              transition: "all 0.3s ease-in-out",
              opacity: isSubmitted ? 0.6 : 1,
              cursor: isSubmitted ? "not-allowed" : "pointer",
              "&:hover": {
                background: !isSubmitted ? "rgba(255, 236, 2, 0.02)" : undefined,
                transform: !isSubmitted ? "translateY(-1px)" : undefined,
              },
            }}
          >
            <Box
              component="img"
              src={YellowIcon}
              alt="Yellow"
              sx={{
                width: '60px',
                height: '60px',
                objectFit: 'contain',
              }}
            />
            <Typography sx={{ 
              fontSize: { xs: "0.9rem", sm: "0.95rem" }, 
              fontWeight: 500,
              color: "var(--color-text-secondary)",
              textAlign: "center"
            }}>
              {getTranslation("Txt758")}
            </Typography>
          </Box>

          <Box
            className={`${
              rating === "RED" ? "selected" : ""
            } feedback-rating-star`}
            onClick={() => {
              if (!isSubmitted && !isSubmitting) {
                submitFeedback("RED");
              }
            }}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
              p: 1.5,
              borderRadius: 2,
              background: rating === "RED" ? "rgba(255, 67, 48, 0.05)" : "transparent",
              border: rating === "RED" ? "1px solid rgba(255, 67, 48, 0.2)" : "1px solid transparent",
              transition: "all 0.3s ease-in-out",
              opacity: isSubmitted ? 0.6 : 1,
              cursor: isSubmitted ? "not-allowed" : "pointer",
              "&:hover": {
                background: !isSubmitted ? "rgba(255, 67, 48, 0.02)" : undefined,
                transform: !isSubmitted ? "translateY(-1px)" : undefined,
              },
            }}
          >
            <Box
              component="img"
              src={RedIcon}
              alt="Red"
              sx={{
                width: '60px',
                height: '60px',
                objectFit: 'contain',
              }}
            />
            <Typography sx={{ 
              fontSize: { xs: "0.9rem", sm: "0.95rem" }, 
              fontWeight: 500,
              color: "var(--color-text-secondary)",
              textAlign: "center"
            }}>
              {getTranslation("Txt757")}
            </Typography>
          </Box>

          </Box>
      </Box>
      </Box>
      <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "row", sm: "row" },
            gap: { xs: 1.5, sm: 2 },
            alignItems: "center",
            justifyContent: "center",
            mt: 0,
          }}
        >
          <Button
            // className="btn btn-next auto-width"
            type="button"
            onClick={() => {
              setSessionId(null);
              setCurrentView(role === 1 ? "distributor-welcome" : "Welcome");
            }}
            variant="contained"
            size="large"
            sx={{
              ...COMMON_MUI_STYLES.primaryCtaButton2,
            }}
          >
            {getTranslation("Txt743")}
          </Button>
        </Box>
      </Box>
    

      {/* PDF Modal with PDF.js */}
      <Modal
        open={showPdfModal}
        onClose={handleClosePdfModal}
        aria-labelledby="pdf-modal"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
         <Box
           sx={{
             position: 'relative',
             width: { xs: '100%', sm: '95%', md: '80%', lg: '70%' },
             height: { xs: '100%', sm: 'auto', md: 'auto' },
             maxHeight: { xs: '100%', sm: '90vh', md: '90vh' },
             maxWidth: '1200px',
             bgcolor: 'background.paper',
             boxShadow: 24,
             borderRadius: { xs: 0, sm: 2 },
             overflow: 'hidden',
             display: 'flex',
             flexDirection: 'column',
           }}
         >
          {/* Header with Close Button */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
              borderBottom: '1px solid #e0e0e0',
              bgcolor: '#f5f5f5',
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              {getTranslation("Txt936")}
            </Typography>
            <IconButton onClick={handleClosePdfModal} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

           {/* PDF Content */}
           <Box 
             sx={{ 
               flex: { xs: 1, sm: '0 1 auto', md: '0 1 auto' },
               position: 'relative', 
               overflow: 'auto',
               WebkitOverflowScrolling: 'touch',
               touchAction: 'pan-y',
               overscrollBehavior: 'contain',
               display: 'flex',
               flexDirection: 'column',
               alignItems: 'center',
               bgcolor: '#525659',
               maxHeight: { xs: '100%', sm: 'calc(90vh - 120px)', md: 'calc(90vh - 120px)' },
             }}
           >
             {pdfLoadError ? (
               <Box
                 sx={{
                   display: 'flex',
                   justifyContent: 'center',
                   alignItems: 'center',
                   height: '100%',
                   p: 3,
                 }}
               >
                 <Typography color="error" align="center">
                   {pdfLoadError}
                 </Typography>
               </Box>
             ) : pdfUrl ? (
               <Box
                 sx={{
                   display: 'flex',
                   flexDirection: 'column',
                   alignItems: 'center',
                   gap: { xs: 1, sm: 2 },
                   width: '100%',
                   p: { xs: 1, sm: 2 },
                 }}
               >
                 <Document
                   file={pdfUrl}
                   onLoadSuccess={onDocumentLoadSuccess}
                   onLoadError={onDocumentLoadError}
                   loading={
                     <Box
                       sx={{
                         display: 'flex',
                         justifyContent: 'center',
                         alignItems: 'center',
                         minHeight: '400px',
                       }}
                     >
                       <CircularProgress />
                     </Box>
                   }
                 >
                   {Array.from(new Array(numPages), (_el, index) => (
                     <Box
                       key={`page_${index + 1}`}
                       sx={{
                         mb: { xs: 1, sm: 2 },
                         boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                       }}
                     >
                       <Page
                         pageNumber={index + 1}
                         width={isMobile ? window.innerWidth - 32 : undefined}
                         height={isMobile ? undefined : window.innerHeight * 0.7}
                         renderTextLayer={true}
                         renderAnnotationLayer={true}
                         loading={
                           <Box
                             sx={{
                               display: 'flex',
                               justifyContent: 'center',
                               alignItems: 'center',
                               minHeight: '400px',
                             }}
                           >
                             <CircularProgress size={24} />
                           </Box>
                         }
                       />
                     </Box>
                   ))}
                 </Document>
               </Box>
             ) : null}
           </Box>
        </Box>
      </Modal>
    </Layout>
  );
};

export default FeedbackPage;
